// client/src/utils/repricePolicy.ts
/**
 * When a new price waits for a person instead of replacing the catalogue's (D119.1).
 *
 * Owner rule (2026-10-13): a vendor-invoice or quote-harvest price that moves MORE than 25% from the
 * price the row holds, up or down, is not applied; it is parked on the row (`pendingReprice`) until a
 * person accepts it. Exactly 25% applies. One number for both writers (the vendor-invoice sync and the
 * quotation harvest) and for the Master Catalogue row that shows the move, which turns the figure red
 * past it. Not per category.
 *
 * Symmetric on purpose: "US$30 became US$300" is usually a typed zero, and a wrongly CHEAP cost is
 * what makes the next quotation lose money without anyone noticing.
 *
 * NOT `packingAlert.PRICE_JUMP_FACTOR` (D46): that is a warning about a per-pack price jump on a
 * delivery, a different question, and it stays as it is.
 *
 * Pure. Registered as BUSINESS_RULES#reprice-ask-threshold.
 *
 * MIRRORED FILE: client/src/utils/repricePolicy.ts is a line-for-line twin. Change one, change both;
 * `mirroredUtilParity.test.js` checks.
 */

export const REPRICE_ASK_ABOVE_PCT = 25;

/**
 * The signed move from `from` to `to`, in percent of `from`: +900 is a tenfold rise, -90 a tenfold fall.
 * Null when there is nothing to measure against: no usable old price (missing, zero or negative) or no
 * new one. Never 0 for "unknown". The caller decides whether the two are in one currency first.
 */
export function repriceChangePct(from: unknown, to: unknown): number | null {
  if (typeof from !== 'number' || typeof to !== 'number') return null;
  if (!Number.isFinite(from) || !Number.isFinite(to) || from <= 0) return null;
  return ((to - from) / from) * 100;
}

/**
 * Whether a move is past the line. Strictly greater, on the size of the move. The size is taken to six
 * decimals first: 4.80 -> 6.00 divides out to 25.000000000000004, and that is exactly 25%.
 * A null (unmeasurable) move is not "too big"; callers with an unmeasurable move have their own rule.
 */
export function movesPastRepriceThreshold(changePct: unknown): boolean {
  if (typeof changePct !== 'number' || !Number.isFinite(changePct)) return false;
  return Math.round(Math.abs(changePct) * 1e6) / 1e6 > REPRICE_ASK_ABOVE_PCT;
}
